import { useCanViewEmails } from '../../../helpers/useCanViewEmails';
import React, { useEffect } from 'react';
import { Tooltip } from '@mui/material';
import cs from 'classnames';

import Avatar from '../../modules/Avatar';
import RolePlate from '../../modules/RolePlate';
import TrashButton from '../../modules/TrashButton';
import { ACCESS_TYPE } from '../../../enums/ACCESS_ENUM';
import { FILE_ACCESS_TYPE, FILE_ACCESS_TYPE_SHARED_WITH_ME_WITH_SUBGROUP, GROUP, USER_NEW } from '../../../enums';
import UsersIcon from '../../../assets/icons/users.svg';

type TUsersList = {
  css: any;
  newUsers: (USER_NEW | GROUP)[];
  accessList: FILE_ACCESS_TYPE[] | FILE_ACCESS_TYPE_SHARED_WITH_ME_WITH_SUBGROUP[];
  onAccessDelete: (accessId: string, accessType?: 'user' | 'group' | 'subgroup', groupId?: string) => void;
  onNewUserDelete: (userId: string) => void;
};

const ShareModalExcludedUserList: React.FC<TUsersList> = ({ newUsers, accessList, onAccessDelete, onNewUserDelete, css }) => {
  const canViewEmails = useCanViewEmails();
  const groupUsersOrganizer = (group: GROUP) => {
    return [...(group?.analysts || []), ...(group?.viewers || [])].filter(
      (value, index, self) => index === self.findIndex((user) => user.id === value.id),
    );
  };

  return (
    <div className={css.list}>
      {newUsers.length > 0 &&
        newUsers.map((obj: USER_NEW | GROUP) => {
          if ('user_name' in obj) {
            return (
              <div
                className={cs(
                  css.listItem,
                  (newUsers.filter((elem) => 'title' in elem) as GROUP[])
                    .map((group) => group.members?.map((member) => member.id))
                    .flat()
                    .includes(obj.user.id)
                    ? css.toDeleteItem
                    : '',
                )}
                key={obj.user.id}
              >
                <div className={css.user}>
                  <Avatar className={css.userAvatar} avatar={obj.user.avatar_url} name={obj.user.email} />
                  <div className={css.userInfo}>
                    <div className={css.userName}>{obj.user?.username}</div>
                    {canViewEmails && (<div className={css.userEmail}>{obj.user.email}</div>)}
                  </div>

                  <div className={css.userRole}>
                    <RolePlate className={css.rolePlate} userRole={obj.role.title} />
                  </div>

                  <div className={css.removeBtn}>
                    <Tooltip title={'Delete'}>
                      <TrashButton small onClick={() => onNewUserDelete(obj.id)} />
                    </Tooltip>
                  </div>
                </div>
              </div>
            );
          } else {
            return (
              <div
                className={cs(
                  css.listItem,
                  (newUsers.filter((group) => 'title' in group) as GROUP[])
                    ?.map((group) => group.subgroups?.map((sub) => sub.id))
                    .flat()
                    .includes(obj.id)
                    ? css.toDeleteItem
                    : '',
                )}
                key={obj.id}
              >
                <div className={css.user}>
                  <Avatar className={css.userAvatar} name={obj.title} />
                  <div className={css.userInfo}>
                    <div className={css.title}>{obj.title}</div>
                    <div className={css.parentGroup}>{obj.parentGroup?.title}</div>
                  </div>

                  <Tooltip
                    placement="right"
                    arrow
                    title={
                      <div className={css.tooltip}>
                        {' '}
                        {obj.members?.map((u) => (
                          <div key={`${u.id}_tooltip`} style={{ display: 'flex', justifyContent: 'space-between' }}>
                            {' '}
                            <span style={{ marginRight: '8px' }}>{u?.username ? u.username : (canViewEmails ? u.email : 'User')} </span>
                          </div>
                        ))}{' '}
                      </div>
                    }
                  >
                    <div className={css.data}>
                      <img src={UsersIcon} alt="users" />
                      {obj.members?.length}
                    </div>
                  </Tooltip>

                  <div className={css.userRole}>
                    <RolePlate className={css.rolePlate} userRole={obj.type!} />
                  </div>

                  <div className={css.removeBtn}>
                    <Tooltip title={'Delete'}>
                      <TrashButton small onClick={() => onNewUserDelete(obj.id)} />
                    </Tooltip>
                  </div>
                </div>
              </div>
            );
          }
        })}
      {accessList.map((accessObject) => {
        if (accessObject.user) {
          return (
            <div
              className={cs(
                css.listItem,
                (newUsers.filter((group) => 'title' in group) as GROUP[])
                  ?.map((group) => group.members?.map((member) => member.id))
                  .flat()
                  .includes(accessObject.user.id)
                  ? css.toDeleteItem
                  : '',
              )}
              key={accessObject.user.id}
            >
              <div className={css.user}>
                <Avatar className={css.userAvatar} avatar={accessObject.user.avatar_url} name={accessObject.user.email} />
                <div className={css.userInfo}>
                  <div className={css.userName}>{accessObject.user?.username}</div>
                  {canViewEmails && (<div className={css.userEmail}>{accessObject.user.email}</div>)}
                </div>
                <div className={css.userRole}>
                  <RolePlate className={css.rolePlate} userRole={accessObject.user.role.title} />
                </div>
                <div className={css.removeBtn}>
                  <Tooltip title={'Delete'}>
                    <TrashButton small onClick={() => onAccessDelete(accessObject.id, 'user')} />
                  </Tooltip>
                </div>
              </div>
            </div>
          );
        } else if (accessObject.group) {
          return (
            <div
              className={cs(
                css.listItem,
                (newUsers.filter((group) => 'title' in group) as GROUP[])
                  ?.map((group) => group.subgroups?.map((sub) => sub.id))
                  .flat()
                  .includes(accessObject.group.id)
                  ? css.toDeleteItem
                  : '',
              )}
              key={accessObject.group.id}
            >
              <div className={css.user}>
                <Avatar className={css.userAvatar} name={accessObject.group.title} />
                <div className={css.userInfo}>
                  <div className={css.username}>{accessObject.group?.title}</div>
                  <div className={css.parentGroup}>{accessObject.group.parent_group?.title}</div>
                </div>
                <Tooltip
                  placement="right"
                  arrow
                  title={
                    <div className={css.tooltip}>
                      {' '}
                      {groupUsersOrganizer(accessObject.group as GROUP).map((u) => (
                        <div key={`${u.id}_tooltip`} style={{ display: 'flex', justifyContent: 'space-between' }}>
                          {' '}
                          <span style={{ marginRight: '8px' }}>{u?.username ? u.username : (canViewEmails ? u.email : 'User')} </span>
                        </div>
                      ))}{' '}
                    </div>
                  }
                >
                  <div className={css.data}>
                    <img src={UsersIcon} alt="users" />
                    {groupUsersOrganizer(accessObject.group as GROUP).length}
                  </div>
                </Tooltip>
                <div className={css.userRole}>
                  <RolePlate className={css.rolePlate} userRole={accessObject.group.type} />
                </div>
                <div className={css.removeBtn}>
                  <Tooltip title={'Delete'}>
                    <TrashButton
                      small
                      onClick={() =>
                        onAccessDelete(
                          accessObject.id,
                          accessObject.group?.type.toLowerCase() as 'user' | 'group' | 'subgroup',
                          accessObject.group?.id,
                        )
                      }
                    />
                  </Tooltip>
                </div>
              </div>
            </div>
          );
        }
      })}
    </div>
  );
};

export default ShareModalExcludedUserList;
